import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Analytics } from "@vercel/analytics/react";
import './App.css';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import './App.css';

const API_URL = 'http://127.0.0.1:10000';
// const API_URL = 'https://bg-remover-back-alxd.onrender.com/';
// const API_URL = 'http://10.210.51.46:10000';


const App = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [imageId, setImageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [imageHistory, setImageHistory] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedModel] = useState('u2netp');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isEraseMode, setIsEraseMode] = useState(false);
  const [textOverlay, setTextOverlay] = useState({ text: '', size: 24, color: '#000000', x: 50, y: 50 });
  const [isCanvasInteractive, setIsCanvasInteractive] = useState(true);
  const [showOriginal, setShowOriginal] = useState(false);
  const dragRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const isPNG = (file) => file && file.type === 'image/png';

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
    setResultImage(null);
    setImageId(null);
    setErrorMsg('');
    setIsCanvasInteractive(false);
    setZoomLevel(1);
    e.target.value = null;
    handleSubmit(file, selectedModel);
  };

  const handleDrop = (e) => {
    if (!isCanvasInteractive) return;
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!isPNG(file)) {
      setErrorMsg('Only PNG images are supported.');
      return;
    }
    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
    setResultImage(null);
    setImageId(null);
    setErrorMsg('');
    setIsCanvasInteractive(false);
    setZoomLevel(1);
    dragRef.current.classList.remove('drag-over');
    handleSubmit(file, selectedModel);
  };

  const handleDragOver = (e) => {
    if (!isCanvasInteractive) return;
    e.preventDefault();
    dragRef.current.classList.add('drag-over');
  };

  const handleDragLeave = () => {
    if (!isCanvasInteractive) return;
    dragRef.current.classList.remove('drag-over');
  };

  // --- Inside your frontend App.js --- //

const fetchImageById = async (id) => {
  const response = await fetch(`${API_URL}/get-image/${id}`);
  if (!response.ok) throw new Error("Failed to fetch image");
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

const handleSubmit = async (file, model) => {
  if (!file) {
    setErrorMsg('Please upload a PNG image first.');
    return;
  }
  setLoading(true);
  const formData = new FormData();
  formData.append('image', file);

  try {
    const response = await fetch(`${API_URL}/remove-bg?model=${model}`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) throw new Error("Failed to process image");

    const data = await response.json();
    if (!data.processed_id) throw new Error("No processed_id returned");

    const processedURL = await fetchImageById(data.processed_id);

    setResultImage(processedURL);
    setImageId(data.processed_id);
    setImageHistory((prev) => [
      ...prev,
      {
        original: previewImage,
        processed: processedURL,
        model,
        imageId: data.processed_id,
        file,
      },
    ]);
  } catch (error) {
    setErrorMsg(error.message);
  } finally {
    setLoading(false);
  }
};

const handleChangeBackground = async () => {
  if (!selectedFile) {
    setErrorMsg('No image selected.');
    return;
  }
  setLoading(true);

  const formData = new FormData();
  formData.append('image', selectedFile);
  const rgb = parseInt(bgColor.slice(1), 16);
  const r = (rgb >> 16) & 255;
  const g = (rgb >> 8) & 255;
  const b = rgb & 255;
  formData.append('bg_color', `${r},${g},${b}`);

  try {
    const response = await fetch(`${API_URL}/change-background`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!data.processed_id) throw new Error("Failed to change background");

    const processedURL = await fetchImageById(data.processed_id);
    setResultImage(processedURL);
  } catch (error) {
    setErrorMsg(error.message);
  } finally {
    setLoading(false);
  }
};

const handleApplyEffect = async (effect) => {
  if (!selectedFile) {
    setErrorMsg('No image available.');
    return;
  }
  setLoading(true);

  const formData = new FormData();
  formData.append('image', selectedFile);
  formData.append('effect', effect);

  try {
    const response = await fetch(`${API_URL}/apply-effect`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!data.processed_id) throw new Error("Failed to apply effect");

    const processedURL = await fetchImageById(data.processed_id);
    setResultImage(processedURL);
  } catch (error) {
    setErrorMsg(error.message);
  } finally {
    setLoading(false);
  }
};

const handleCreateDesign = async () => {
  if (!selectedFile) {
    setErrorMsg('No image available.');
    return;
  }
  setLoading(true);

  const formData = new FormData();
  formData.append('image', selectedFile);
  formData.append('text', textOverlay.text);
  formData.append('size', textOverlay.size);
  formData.append('color', textOverlay.color);
  formData.append('x', textOverlay.x);
  formData.append('y', textOverlay.y);

  try {
    const response = await fetch(`${API_URL}/add-text`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!data.processed_id) throw new Error("Failed to add text");

    const processedURL = await fetchImageById(data.processed_id);
    setResultImage(processedURL);
  } catch (error) {
    setErrorMsg(error.message);
  } finally {
    setLoading(false);
  }
};
  const handleDeleteImage = (index) => {
    setImageHistory((prev) => prev.filter((_, i) => i !== index));
    if (imageHistory[index].processed === resultImage) {
      setResultImage(null);
      setSelectedFile(null);
      setPreviewImage(null);
      setImageId(null);
      setIsCanvasInteractive(true);
      setZoomLevel(1);
    }
  };

  const handleHistoryClick = (item) => {
    setResultImage(item.processed);
    setPreviewImage(item.original);
    setSelectedFile(item.file);
    setImageId(item.imageId);
    setIsCanvasInteractive(false);
    setErrorMsg('');
    setZoomLevel(1);
  };

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setZoomLevel(1);

  const handleUploadClick = () => {
    setIsCanvasInteractive(true);
    setResultImage(null);
    setSelectedFile(null);
    setPreviewImage(null);
    setImageId(null);
    setErrorMsg('');
    setIsEraseMode(false);
    setShowOriginal(false);
    setZoomLevel(1);
    if (canvasRef.current) canvasRef.current.clearCanvas();
    fileInputRef.current.click();
  };

  return (
    <div className="app">
      {/* Header */}
      <Analytics />
      <header className="header">
        <div className="logo">Bit-BG</div>
      </header>

      {/* Main Content */}
      <div className="main-content">
        {/* History Panel */}
        <div className="history-panel">
          <div className="history-header">
            <h3 className="history-title">Your Workspace</h3>
          </div>
          <div className="history-list">
            {imageHistory.map((image, idx) => (
              <div key={idx} className="history-card" onClick={() => handleHistoryClick(image)}>
                <img src={image.processed} alt="Processed" className="thumbnail-img" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteImage(idx);
                  }}
                  className="delete-button"
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            ))}
            {imageHistory.length === 0 && (
              <p className="history-empty">No images available.</p>
            )}
          </div>
        </div>

        {/* Image Panel */}
        <div className="image-panel">
          <div
            ref={dragRef}
            className={`canvas-container ${resultImage ? 'transparent-bg' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <input
              type="file"
              accept="image/*"
              id="fileUpload"
              ref={fileInputRef}
              hidden
              onChange={handleFileChange}
            />
            <label
              className={`canvas-label ${isCanvasInteractive ? '' : 'non-interactive'}`}
              htmlFor="fileUpload"
            >
              <AnimatePresence mode="wait">
                {isEraseMode ? (
                  <div className="relative-image">
                    <img src={resultImage} alt="Processed" className="canvas-image" />
                    <ReactSketchCanvas
                      ref={canvasRef}
                      className="canvas-overlay"
                      strokeWidth={10}
                      strokeColor="#000000"
                      backgroundImage={resultImage}
                      canvasColor="transparent"
                    />
                  </div>
                ) : loading ? (
                  <motion.div
                    key="loading"
                    className="peeling-animation"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <img src={previewImage} alt="Preview" className="canvas-image" />
                    <div className="peel-effect"></div>
                  </motion.div>
                ) : showOriginal && previewImage ? (
                  <motion.div
                    key="original"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="relative-image"
                  >
                    <img src={previewImage} alt="Original" className="canvas-image" />
                  </motion.div>
                ) : resultImage ? (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="relative-image"
                  >
                    <img
                      src={resultImage}
                      alt="Result"
                      className="canvas-image"
                      style={{ transform: `scale(${zoomLevel})` }}
                    />
                    {textOverlay.text && (
                      <span
                        className="text-overlay"
                        style={{
                          left: Math.min(textOverlay.x, 500),
                          top: Math.min(textOverlay.y, 390),
                          fontSize: Math.min(textOverlay.size, 50),
                          color: textOverlay.color,
                          fontFamily: 'Arial',
                        }}
                      >
                        {textOverlay.text}
                      </span>
                    )}
                  </motion.div>
                ) : previewImage ? (
                  <motion.div
                    key="preview"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="relative-image"
                  >
                    <img src={previewImage} alt="Preview" className="canvas-image" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="placeholder"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="upload-placeholder"
                  >
                    <p>Upload a PNG using the + button</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </label>
          </div>
          {errorMsg && <p className="error-message">{errorMsg}</p>}
          {(resultImage || previewImage) && (
            <div className="edit-tools">
              <div className="zoom-controls">
                <button
                  onClick={handleZoomOut}
                  className="zoom-btn"
                  title="Zoom Out"
                  disabled={zoomLevel <= 0.5}
                >
                  -
                </button>
                <button
                  onClick={handleResetZoom}
                  className="zoom-btn"
                  title="Reset Zoom"
                >
                  ↔
                </button>
                <button
                  onClick={handleZoomIn}
                  className="zoom-btn"
                  title="Zoom In"
                  disabled={zoomLevel >= 2}
                >
                  +
                </button>
                {resultImage && (
                  <button
                    onMouseDown={() => setShowOriginal(true)}
                    onMouseUp={() => setShowOriginal(false)}
                    onMouseLeave={() => setShowOriginal(false)}
                    className="zoom-btn"
                    title="Show Original"
                  >
                    👁️
                  </button>
                )}
              </div>
              {resultImage && (
                <a
                  href={resultImage}
                  download="processed-image.png"
                  className="btn-primary"
                >
                  Download
                </a>
              )}
            </div>
          )}
        </div>

        {/* Editing Tools Panel */}
        <motion.div
          className="editing-panel"
          initial={{ opacity: 1 }}
          animate={{
            opacity: isSidebarOpen ? 1 : 0,
            display: isSidebarOpen ? 'block' : 'none',
            transitionEnd: { display: isSidebarOpen ? 'block' : 'none' },
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="tools">
            <div className="tools-header">
              <h3 className="tools-heading">Editing Tools</h3>
            </div>
            <div className="tool-group">
              <label className="tool-label">Background Color</label>
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="input-color"
              />
              <button
                onClick={handleChangeBackground}
                className="btn-primary"
              >
                Change Background
              </button>
            </div>
            <div className="tool-group">
              {isEraseMode && (
                <button
                  onClick={() => {
                    setIsEraseMode(false);
                    if (canvasRef.current) canvasRef.current.clearCanvas();
                  }}
                  className="btn-danger"
                >
                  Cancel
                </button>
              )}
            </div>
            <div className="tool-group">
              <label className="tool-label">Effects</label>
              <button
                onClick={() => handleApplyEffect('grayscale')}
                className="btn-secondary"
              >
                Grayscale
              </button>
              <button
                onClick={() => handleApplyEffect('blur')}
                className="btn-secondary"
              >
                Blur
              </button>
            </div>
            <div className="tool-group">
              <label className="tool-label">Apply Text</label>
              <input
                type="text"
                placeholder="Enter text"
                value={textOverlay.text}
                onChange={(e) => setTextOverlay({ ...textOverlay, text: e.target.value })}
                className="input-text"
              />
              <input
                type="number"
                value={textOverlay.size}
                onChange={(e) => setTextOverlay({ ...textOverlay, size: parseInt(e.target.value) })}
                className="input-size"
              />
              <input
                type="color"
                value={textOverlay.color}
                onChange={(e) => setTextOverlay({ ...textOverlay, color: e.target.value })}
                className="input-color"
              />
              <button
                onClick={handleCreateDesign}
                className="btn-primary"
              >
                Apply Text
              </button>
            </div>
          </div>
        </motion.div>
       
      </div>
      <header className="header">
          <div className="logo">Bit-BG © {new Date().getFullYear()} -{" "}
            <a
              href="https://www.bitknob.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Bitknob Innovations
            </a></div>
        </header>
      {/* Upload Button */}
      <button
        className="upload-btn"
        onClick={handleUploadClick}
      >
        +
      </button>


    </div>
  );
};

export default App;
